import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, UserRound } from 'lucide-react';
import Header from '../Common/Header';
import { Footer } from '../Common';
import Hyperlink from '@ajat/design-system/Hyperlink';
import InlineNotice from '@ajat/design-system/InlineNotice';
import { useCart } from '../../../../context/CartContext';
import bannerImage from '../../../../assets/abc-se/checkout/thank-you-banner.jpg';
import '../../Common/Checkout/ThankYou.css';

const BRAND_SLUG = 'abcse';

const SS_KEY = (orderNumber) => `abcse:thankyou:${orderNumber}`;

const STATUS_STYLES = {
  success:   { variant: 'success', title: 'Tack!' },
  pending:   { variant: 'warning', title: 'Tack!' },
  cancelled: { variant: 'warning', title: 'Betalningen avbröts' },
  failed:    { variant: 'error', title: 'Betalningen misslyckades' },
};

const ThankYou = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { confirmPayment } = useCart(BRAND_SLUG);

  const urlOrderNumber = searchParams.get('order');
  const urlStatus = searchParams.get('status');
  const providerRef = searchParams.get('ref') || searchParams.get('providerRef');

  const stateOrder = location.state?.order || null;
  const orderNumber =
    urlOrderNumber || stateOrder?.orderNumber || location.state?.orderNumber || null;

  const [snapshot, setSnapshot] = useState(() => {
    if (!orderNumber) return stateOrder;
    try {
      const raw = sessionStorage.getItem(SS_KEY(orderNumber));
      if (raw) return JSON.parse(raw);
    } catch { /* noop */ }
    return stateOrder;
  });
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!orderNumber) return;
    const key = SS_KEY(orderNumber);
    let cached = null;
    try { cached = JSON.parse(sessionStorage.getItem(key) || 'null'); } catch { /* noop */ }
    const finalStatuses = ['paid', 'pending_invoice', 'cancelled', 'failed'];
    if (cached && finalStatuses.includes(cached.paymentStatus)) {
      setSnapshot(cached);
      return;
    }
    setConfirming(true);
    confirmPayment(orderNumber, urlStatus, providerRef)
      .then((data) => {
        setSnapshot(data);
        try {
          const safe = { ...data };
          delete safe.payment_payload;
          sessionStorage.setItem(key, JSON.stringify(safe));
        } catch { /* noop */ }
      })
      .catch((err) => console.warn('Payment confirm failed', err))
      .finally(() => setConfirming(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-brand');
    document.documentElement.setAttribute('data-brand', 'ABC');
    return () => {
      if (prev) document.documentElement.setAttribute('data-brand', prev);
      else document.documentElement.removeAttribute('data-brand');
    };
  }, []);

  const status = useMemo(() => {
    const ps = snapshot?.paymentStatus;
    if (ps === 'paid' || ps === 'pending_invoice') return 'success';
    if (ps === 'cancelled') return 'cancelled';
    if (ps === 'failed') return 'failed';
    if (urlStatus === 'cancelled') return 'cancelled';
    if (urlStatus === 'failed') return 'failed';
    return 'pending';
  }, [snapshot?.paymentStatus, urlStatus]);

  const styleSet = STATUS_STYLES[status];
  const message = snapshot?.message;

  return (
    <div className="thank-you">
      <Header />

      <main className="thank-you__main">
        <section className="thank-you__banner">
          <div className="thank-you__banner-inner">
            <div className="thank-you__card">
              {orderNumber && (
                <p className="thank-you__order typo-h4">Order #{orderNumber}</p>
              )}
              <h1 className="thank-you__title typo-h2-editorial">{styleSet.title}</h1>

              {confirming && (
                <p className="typo-body">Verifierar betalning…</p>
              )}


              {message && (
                <InlineNotice variant={styleSet.variant} size="s" textVariant="body" role="status" className="thank-you__info-box">
                  {message}
                </InlineNotice>
              )}

              {(status === 'cancelled' || status === 'failed') && (
                <div style={{ marginTop: 16 }}>
                  <Hyperlink
                    variant="regular"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/clothing/abc-se/shopping-cart');
                    }}
                  >
                    Tillbaka till varukorgen
</Hyperlink>
                </div>
              )}

              {status === 'success' && (
                <div className="thank-you__features">
                  <div className="thank-you__feature">
                    <div className="thank-you__feature-icon">
                      <Mail size={24} strokeWidth={1.5} />
                    </div>
                    <p className="thank-you__feature-text typo-body">
                      Du får ett e-postmeddelande med orderdetaljer och en länk för att redigera din beställning
                    </p>
                  </div>

                  <div className="thank-you__feature">
                    <div className="thank-you__feature-icon">
                      <UserRound size={24} strokeWidth={1.5} />
                    </div>
                    <p className="thank-you__feature-text typo-body">
                      Enligt våra{' '}
                      <Hyperlink
                        variant="regular"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/clothing/abc-se/terms');
                        }}
                        className="thank-you__terms-link"
                      >
                        köpvillkor
                      </Hyperlink>{' '}
                      har vi skapat ett konto åt dig så att du kan hantera din beställning
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="thank-you__visual">
              <img
                src={bannerImage}
                alt="Glada studenter"
                className="thank-you__visual-img"
              />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ThankYou;
